import React, { useState, useEffect, useCallback } from 'react';
import { Form, Item } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { DateBox } from 'devextreme-react/date-box';
import { TagBox } from 'devextreme-react/tag-box';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { useMsal } from "@azure/msal-react";
import { getApiToken } from '../../utils/authUtils.js';
import notify from 'devextreme/ui/notify';
import { Item as ToolbarItem, Toolbar } from 'devextreme-react/toolbar';
import ScrollView from 'devextreme-react/scroll-view';
import { useNavigate } from 'react-router-dom';
import './report4.scss';
import { openPDFOnDevice } from '../../utils/pdfUtils.js';

export default function Page() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [trainingGroupsData, setTrainingGroupsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    traininggroup_ids: [],
    startdate: new Date(new Date().getFullYear(), 0, 1), // January 1st of current year
    enddate: new Date() // Today
  });

  const onLeftClick = () => {
    navigate(-1);
  };

  // Load initial data
  useEffect(() => {
    getApiToken(instance, accounts).then(token => {
      const trainingGroupsStore = createStore({
        key: 'traininggroupId',
        loadUrl: `${process.env.REACT_APP_serverEndpoint}/Traininggroup/TraininggroupsBasic`,
        onBeforeSend: (method, ajaxOptions) => {
          ajaxOptions.headers = {
            'Authorization': "Bearer " + token
          };
        },
      });

      trainingGroupsStore.load().then(data => {
        setTrainingGroupsData(data);
      }).catch(error => {
        console.error('Error loading training groups:', error);
      });
    });
  }, [instance, accounts]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const generateReport = useCallback(() => {
    if (!formData.traininggroup_ids.length) {
      notify({
        message: 'Bitte wählen Sie mindestens eine Trainingsgruppe aus.',
        position: { at: 'bottom center', my: 'bottom center' }
      }, 'warning');
      return;
    }

    setIsLoading(true);
    getApiToken(instance, accounts).then(token => {
      const requestBody = {
        traininggroup_ids: formData.traininggroup_ids,
        startdate: formData.startdate.toISOString(),
        enddate: formData.enddate.toISOString()
      };

      fetch(`${process.env.REACT_APP_serverEndpoint}/Report/GetSquadsReport`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(async blob => {
        var filename = `Kaderübersicht_${new Date().toISOString().split('T')[0]}.pdf`;
        await openPDFOnDevice(blob, filename);

        notify({
          message: 'PDF-Bericht wurde erfolgreich heruntergeladen.',
          position: { at: 'bottom center', my: 'bottom center' }
        }, 'success');
      })
      .catch(error => {
        console.error('Error generating report:', error);
        notify({
          message: 'Fehler beim Generieren des Berichts.',
          position: { at: 'bottom center', my: 'bottom center' }
        }, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    });
  }, [formData, instance, accounts]);

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-report-details'>
        <Toolbar className='toolbar-details theme-dependent'>
          <ToolbarItem location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={onLeftClick} />
          </ToolbarItem>
          <ToolbarItem location='before' text='Kader' />
          <ToolbarItem location='after'>
            <Button
              text='Bericht erstellen'
              type='default'
              icon='pdffile'
              onClick={generateReport}
              disabled={isLoading}
            />
          </ToolbarItem>
        </Toolbar>

        <div className='panels'>
          <Form formData={formData} labelLocation="top" className='report-form' colCount={3}>
            <Item dataField="traininggroup_ids" label={{ text: 'Trainingsgruppen' }}>
              <TagBox
                dataSource={trainingGroupsData}
                valueExpr="traininggroupId"
                displayExpr="groupname"
                placeholder="Trainingsgruppen auswählen..."
                showSelectionControls={true}
                value={formData.traininggroup_ids}
                onValueChanged={(e) => handleFieldChange('traininggroup_ids', e.value)}
              />
            </Item>

            <Item dataField="startdate" label={{ text: 'Von' }}>
              <DateBox
                type="date"
                displayFormat="dd.MM.yyyy"
                placeholder="Von"
                value={formData.startdate}
                onValueChanged={(e) => handleFieldChange('startdate', e.value)}
                useMaskBehavior={true}
              />
            </Item>

            <Item dataField="enddate" label={{ text: 'Bis' }}>
              <DateBox
                type="date"
                displayFormat="dd.MM.yyyy"
                placeholder="Bis"
                value={formData.enddate}
                onValueChanged={(e) => handleFieldChange('enddate', e.value)}
                useMaskBehavior={true}
              />
            </Item>
          </Form>
          {isLoading && (
            <div className='loading-info'>
              <p>Bericht wird generiert...</p>
            </div>
          )}
        </div>
      </div>
    </ScrollView>
  );
}
